import { create } from 'zustand';
import {
  ROLE_HOME_ROUTE,
  type AuthSession,
  type Permission,
  type PublicUser,
  type RegisterRequest,
  type UserRole,
} from '@tutor/shared';
import { api, setAccessToken, setSessionListener } from '../lib/api-client';

export type AuthStatus = 'idle' | 'restoring' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: PublicUser | null;
  permissions: Permission[];

  login: (input: { email: string; password: string }) => Promise<PublicUser>;
  /** Self-registration. The API signs the new account in, so this returns a session. */
  register: (input: RegisterRequest & Record<string, unknown>) => Promise<PublicUser>;
  logout: () => Promise<void>;
  /** Called once on boot: trades the httpOnly refresh cookie for a live session. */
  restore: () => Promise<void>;
  applySession: (session: AuthSession | null) => void;

  can: (permission: Permission) => boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  homeRoute: () => string;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'idle',
  user: null,
  permissions: [],

  applySession(session) {
    if (!session) {
      setAccessToken(null);
      set({ status: 'unauthenticated', user: null, permissions: [] });
      return;
    }

    setAccessToken(session.accessToken);
    set({ status: 'authenticated', user: session.user, permissions: session.permissions });
  },

  async login(input) {
    const session = await api.post<AuthSession>('/api/auth/login', input);
    get().applySession(session);
    return session.user;
  },

  async register(input) {
    const session = await api.post<AuthSession>('/api/auth/register', input);
    get().applySession(session);
    return session.user;
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      // Whatever the server said, the local session is over.
      get().applySession(null);
    }
  },

  async restore() {
    set({ status: 'restoring' });
    try {
      const session = await api.post<AuthSession>('/api/auth/refresh', undefined, {
        skipAuthRefresh: true,
      });
      get().applySession(session);
    } catch {
      // No cookie, or it expired — a first-time visitor, not an error worth showing.
      get().applySession(null);
    }
  },

  can(permission) {
    return get().permissions.includes(permission);
  },

  hasRole(...roles) {
    const role = get().user?.role;
    return role ? roles.includes(role) : false;
  },

  homeRoute() {
    const role = get().user?.role;
    return role ? (ROLE_HOME_ROUTE[role] ?? '/') : '/login';
  },
}));

// The API client refreshes tokens on its own; this keeps the store in step when it does.
setSessionListener((session) => {
  useAuthStore.getState().applySession(session);
});
